import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Sort,
  Page,
  Selection,
  Inject,
} from '@syncfusion/ej2-react-grids';

import { useAppSelector } from '../../../../../app/store';
import { ViewNoteDialog } from '../../dialogs';
import { NoteView } from '../../../../../services/RefundService/types/refundTypes';
import { useNoteViewDialog } from './hooks';
import { DateTemplate, TextTemplate } from './templates';

export interface NotesGridProps {
  /** Optional ID for testing */
  testId?: string;
}

/**
 * NotesGrid - Displays refund notes with view dialog
 * 
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for dialog state
 * - Clean component structure
 */
export const NotesGrid: React.FC<NotesGridProps> = ({ testId }) => {
  const { currentRefund } = useAppSelector((state) => state.refund);
  const notes = currentRefund?.notes || [];

  // Sort notes with newest first
  const sortedNotes = useMemo(
    () =>
      [...notes].sort(
        (a, b) => new Date(b.addDate || '').getTime() - new Date(a.addDate || '').getTime()
      ),
    [notes]
  );

  // Use custom hook for dialog state
  const { selectedNote, showViewDialog, handleRowSelection, handleClose } = useNoteViewDialog<NoteView>();

  // Create date template
  const dateTemplate = useMemo(
    () => (props: any) => <DateTemplate addDate={props.addDate} />,
    []
  );

  // Create text template
  const textTemplate = useMemo(
    () => (props: any) => <TextTemplate text={props.text} />,
    []
  );

  return (
    <Box sx={{ height: '400px', width: '100%', maxWidth: '800px' }}>
      <GridComponent
        dataSource={sortedNotes}
        allowSorting={true}
        allowPaging={true}
        pageSettings={{ pageSize: 10 }}
        selectionSettings={{ mode: 'Row', type: 'Single' }}
        rowSelected={handleRowSelection}
        height="100%"
        data-testid={testId}
      >
        <ColumnsDirective>
          <ColumnDirective
            field="addDate"
            headerText="Date Added"
            width="180"
            template={dateTemplate}
          />
          <ColumnDirective
            field="addedBy"
            headerText="Added By"
            width="150"
          />
          <ColumnDirective
            field="text"
            headerText="Note Text"
            template={textTemplate}
          />
        </ColumnsDirective>
        <Inject services={[Sort, Page, Selection]} />
      </GridComponent>

      {/* ViewNoteDialog - Self-contained within the grid */}
      {showViewDialog && selectedNote && (
        <ViewNoteDialog
          open={showViewDialog}
          note={selectedNote}
          onClose={handleClose}
        />
      )}
    </Box>
  );
};

export default NotesGrid;
